import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  Spinner,
  Banner,
  InlineStack,
  Divider,
  Button,
  Badge,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useDashboardStats } from '@/queries/dashboard';
import { useNotificationOverview } from '@/queries/notifications';
import { ApiError } from '@/types/api';

function money(n: number, currency: string): string {
  const sym = currency === 'EGP' ? 'EGP ' : currency === 'USD' ? '$' : `${currency} `;
  return `${sym}${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" tone="subdued" variant="bodySm">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

function PushOverviewWidget() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useNotificationOverview();

  // Tiny inline sparkline using SVG — no extra deps, scales with the card.
  const renderSparkline = (values: number[]) => {
    if (!values.length) return null;
    const max = Math.max(...values, 1);
    const w = 140;
    const h = 32;
    const step = w / (values.length - 1 || 1);
    const points = values
      .map((v, i) => `${i * step},${h - (v / max) * h}`)
      .join(' ');
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <polyline
          fill="none"
          stroke="var(--p-color-text-info)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    );
  };

  const pct = (n: number, d: number): string =>
    d > 0 ? `${Math.round((n / d) * 100)}%` : '—';

  const formatTime = (epoch: number | null | undefined): string => {
    if (!epoch) return '—';
    try { return new Date(epoch * 1000).toLocaleString(); } catch { return '—'; }
  };

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingSm">Push notifications</Text>
            <Text as="span" tone="subdued" variant="bodySm">آخر 7 أيام</Text>
          </InlineStack>
          <Button
            variant="plain"
            onClick={() => navigate('/push-notifications')}
          >
            افتح الـ panel →
          </Button>
        </InlineStack>
        <Divider />
        {isLoading ? (
          <InlineStack align="center"><Spinner accessibilityLabel="Loading" size="small" /></InlineStack>
        ) : error ? (
          <Banner tone="warning"><p>الـ overview غير متاح حاليًا</p></Banner>
        ) : data ? (
          <BlockStack gap="300">
            <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">Active devices</Text>
                <Text as="p" variant="headingMd">{data.active_devices}</Text>
              </Box>
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">Sent</Text>
                <Text as="p" variant="headingMd">{data.last_7_days.sent}</Text>
              </Box>
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">
                  Opened ({pct(data.last_7_days.opened, data.last_7_days.sent)})
                </Text>
                <Text as="p" variant="headingMd">{data.last_7_days.opened}</Text>
              </Box>
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">
                  Clicked ({pct(data.last_7_days.clicked, data.last_7_days.sent)})
                </Text>
                <Text as="p" variant="headingMd">{data.last_7_days.clicked}</Text>
              </Box>
            </InlineGrid>

            <InlineStack align="space-between" blockAlign="center" gap="400">
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">Daily sends</Text>
                {renderSparkline(data.sparkline)}
              </Box>
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">Active schedules</Text>
                <Text as="p" variant="headingMd">{data.active_schedules}</Text>
              </Box>
              <Box>
                <Text as="p" tone="subdued" variant="bodySm">Conversions</Text>
                <Text as="p" variant="headingMd">{data.last_7_days.converted}</Text>
              </Box>
            </InlineStack>

            {data.latest ? (
              <Box
                background="bg-surface-secondary"
                padding="300"
                borderRadius="200"
              >
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodySm" tone="subdued">
                      آخر notification:
                    </Text>
                    <Badge tone="info">{formatTime(data.latest.created_at)}</Badge>
                  </InlineStack>
                  <Text as="p" fontWeight="medium">{data.latest.title}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {data.latest.sent_count} sent · {data.latest.opened_count} opened · {data.latest.clicked_count} clicked
                  </Text>
                </BlockStack>
              </Box>
            ) : (
              <Banner tone="info">
                <p>لسه مفيش notifications مبعوته.</p>
              </Banner>
            )}

            <InlineStack align="end">
              <Button
                variant="primary"
                onClick={() => navigate('/push-notifications')}
              >
                ابعت notification جديدة
              </Button>
            </InlineStack>
          </BlockStack>
        ) : null}
      </BlockStack>
    </Card>
  );
}

export function DashboardHome() {
  const { data, isLoading, error } = useDashboardStats();

  return (
    <Page
      title="Dashboard"
      subtitle="Last 30 days"
      primaryAction={{
        content: 'Open theme editor',
        onAction: () => {
          window.location.href = '/';
        },
      }}
    >
      <Layout>
        <Layout.Section>
          {isLoading && (
            <Box padding="600">
              <InlineStack align="center" gap="200">
                <Spinner accessibilityLabel="Loading stats" size="small" />
                <Text as="p">Loading store stats…</Text>
              </InlineStack>
            </Box>
          )}

          {error && (
            <Banner tone="critical" title="Could not load stats">
              {(error as ApiError)?.message ?? 'Stats unavailable'}
            </Banner>
          )}

          {data && (
            <BlockStack gap="400">
              <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
                <Stat
                  label="Total sales"
                  value={money(data.total_sales, data.currency)}
                />
                <Stat label="Orders" value={String(data.order_count)} />
                <Stat
                  label="Avg order value"
                  value={money(data.avg_order_value, data.currency)}
                />
              </InlineGrid>

              <PushOverviewWidget />

              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    Top products (units sold)
                  </Text>
                  <Divider />
                  {data.top_products.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No sales in the last 30 days.
                    </Text>
                  ) : (
                    <BlockStack gap="200">
                      {data.top_products.map((p: { title: string; units: number }, i: number) => (
                        <InlineStack key={p.title} align="space-between">
                          <Text as="span">
                            {i + 1}. {p.title}
                          </Text>
                          <Text as="span" fontWeight="semibold">
                            {p.units}
                          </Text>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>

              <Text as="p" tone="subdued" variant="bodySm">
                30-day snapshot from Shopify Admin API (most recent 250 orders
                in the window).
              </Text>
            </BlockStack>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
